'use client';

import React from 'react';
import { Wifi, WifiOff, Search, Circle } from 'lucide-react';
import { ConnectionStatus as Status } from '@/store/chatStore';
import { cn } from '@/lib/utils';

interface ConnectionStatusProps {
  status: Status;
  genderFilter?: 'FEMALE' | null;
  className?: string;
}

const config: Record<
  Status,
  { label: string; icon: React.FC<{ size?: number | string; className?: string }>; color: string; dot: string; animate: boolean }
> = {
  idle: {
    label: 'Not connected',
    icon: WifiOff,
    color: 'text-brand-gray',
    dot: 'bg-brand-gray',
    animate: false,
  },
  searching: {
    label: 'Finding stranger...',
    icon: Search,
    color: 'text-yellow-400',
    dot: 'bg-yellow-400',
    animate: true,
  },
  connected: {
    label: 'Connected',
    icon: Wifi,
    color: 'text-brand-lime',
    dot: 'bg-brand-lime',
    animate: false,
  },
  disconnected: {
    label: 'Disconnected',
    icon: WifiOff,
    color: 'text-brand-red',
    dot: 'bg-brand-red',
    animate: false,
  },
};

export function ConnectionStatus({ status, genderFilter, className }: ConnectionStatusProps) {
  const { label, icon: Icon, color, dot, animate } = config[status];

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Dot indicator */}
      <span className="relative flex h-2.5 w-2.5">
        {animate && (
          <span
            className={cn(
              'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
              dot
            )}
          />
        )}
        <span className={cn('relative inline-flex rounded-full h-2.5 w-2.5', dot)} />
      </span>

      {/* Icon + Label */}
      <div className="flex items-center gap-1.5">
        <Icon size={13} className={color} />
        <span className={cn('text-xs font-medium', color)}>
          {genderFilter === 'FEMALE' && status === 'searching' ? 'Finding female stranger...' : label}
        </span>
      </div>
    </div>
  );
}
